import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Routes, Route, Navigate } from "react-router-dom";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Divider,
  FormGroup,
  FormControlLabel,
  Switch,
  Button,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import PaymentConfiguration from "../components/settings/PaymentConfiguration";
import servicesService from "../services/ServicesService";
import { errorMessage } from "../shared/utils/validators";
import { IntegrationDialog } from "../components/Dialog";
import FormField from "../components/FormField";
import WhatsappEmbeddedSyncContent from "../components/WhatsappEmbeddedSyncContent";
import useWhatsappEmbeddedSyncState from "../hooks/useWhatsappEmbeddedSyncState";
import CallSettings from "../components/settings/CallSettings";
import ApiKeysSettings from "../components/settings/ApiKeysSettings";


const SETTINGS_TABS = [
  { id: "payment-configuration", label: "Payment Configuration", path: "payment-configuration" },
  { id: "message-settings", label: "Message Settings", path: "message-settings" },
  { id: "catalog", label: "Catalog", path: "catalog" },
  { id: "whatsapp-sync", label: "WhatsApp sync", path: "whatsapp-sync" },
  { id: "call-settings", label: "Call Settings", path: "call-settings" },
  { id: "api-keys", label: "API keys", path: "api-keys" },
];

function CatalogSettings() {
  const [catalogId, setCatalogId] = useState("");
  const [isCatalogDialogOpen, setIsCatalogDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [indexLoading, setIndexLoading] = useState(false);
  const [visualSettings, setVisualSettings] = useState(null);
  const [isFacebookConnected, setIsFacebookConnected] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchConnectionState = async () => {
    try {
      const [whatsappModel, connectedCatalogId, visual] = await Promise.all([
        servicesService.getWhatsapp(),
        servicesService.getCatalog().catch(() => null),
        servicesService.getVisualSearchSettings().catch(() => null),
      ]);
      setIsFacebookConnected(whatsappModel?.getIsEmbeddedSignup?.() === 1);
      setCatalogId(connectedCatalogId || "");
      setVisualSettings(visual);
    } catch (e) {
      setIsFacebookConnected(false);
    }
  };

  useEffect(() => {
    fetchConnectionState();
  }, []);

  const openFacebookConnect = () => {
    window.dispatchEvent(new Event("open-connect-facebook"));
  };

  const openCatalogConnect = () => {
    setIsCatalogDialogOpen(true);
  };

  const connectCatalog = async () => {
    setLoading(true);
    try {
      await servicesService.creatCatalog(catalogId);
      setSnackbar({
        open: true,
        message: "Catalog connected successfully",
        severity: "success",
      });
      setIsCatalogDialogOpen(false);
      fetchConnectionState();
    } catch (e) {
      setSnackbar({
        open: true,
        message: errorMessage(e) || "Catalog not connected",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 700 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Catalog
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Connect your catalog for commerce and product messaging.
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={openCatalogConnect}>
          {catalogId ? `Catalog Connected (${catalogId})` : "Connect Catalog"}
        </Button>
      </Stack>

      <Box sx={{ mt: 3, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Visual product search (CLIP)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Index catalog images once, then match customer photos on WhatsApp via an automation rule.
        </Typography>
        {!catalogId ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Connect your Meta catalog above to enable indexing and visual search.
          </Typography>
        ) : null}
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(visualSettings?.visual_search_enabled)}
              disabled={!catalogId}
              onChange={async (e) => {
                try {
                  const updated = await servicesService.updateVisualSearchSettings({
                    visual_search_enabled: e.target.checked,
                  });
                  setVisualSettings(updated);
                } catch (err) {
                  setSnackbar({
                    open: true,
                    message: errorMessage(err) || "Failed to update setting",
                    severity: "error",
                  });
                }
              }}
            />
          }
          label="Enable visual product search"
        />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Indexed images: {visualSettings?.indexed_image_count ?? 0}
          {visualSettings?.visual_indexed_at
            ? ` · Last index: ${new Date(visualSettings.visual_indexed_at).toLocaleString()}`
            : ""}
        </Typography>
        {visualSettings?.index_status ? (
          <Typography variant="caption" display="block" color="text.secondary">
            Variants: {visualSettings.index_status.variants ?? 0} · Skipped (no image):{" "}
            {visualSettings.index_status.skippedNoImage ?? 0}
          </Typography>
        ) : null}
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          disabled={!catalogId || indexLoading || !visualSettings?.visual_search_enabled}
          onClick={async () => {
            setIndexLoading(true);
            try {
              const result = await servicesService.buildVisualCatalogIndex();
              setSnackbar({
                open: true,
                message: `Index complete: ${result?.indexed ?? 0} images`,
                severity: "success",
              });
              await fetchConnectionState();
            } catch (err) {
              setSnackbar({
                open: true,
                message: errorMessage(err) || "Index failed",
                severity: "error",
              });
            } finally {
              setIndexLoading(false);
            }
          }}
        >
          {indexLoading ? "Building index…" : "Build / rebuild image index"}
        </Button>
      </Box>

      <IntegrationDialog
        isOpen={isCatalogDialogOpen}
        title="Connect Catalog"
        onCancel={() => setIsCatalogDialogOpen(false)}
        content={() => (
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Please enter your Meta catalog ID connected with your WhatsApp business number.
            </Typography>
            <FormField
              type="text"
              label="Catalog ID"
              isRequired
              name="catalogId"
              value={catalogId}
              onChange={(e) => setCatalogId(e.target.value)}
              error={catalogId && catalogId.length < 5 ? "Enter valid catalog ID" : null}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button onClick={() => setIsCatalogDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={connectCatalog}
                disabled={loading || !catalogId || catalogId.length < 5}
              >
                Connect
              </Button>
            </Stack>
          </Box>
        )}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function MessageSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [settings, setSettings] = useState({
    id: null,
    isTypingIndicatorEnabled: false,
    isBlueTickEnabled: false,
    isChatSimulationEnabled: false,
  });

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const data = await servicesService.getServiceSettings();
        if (isMounted && data) {
          setSettings(data);
        }
      } catch (e) {
        // ignore for now or add notification system hook here
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleToggleChange = (field) => (event) => {
    setSettings((prev) => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await servicesService.saveServiceSettings(settings);
      setSettings(updated);
      setSnackbar({
        open: true,
        message: "Settings saved successfully",
        severity: "success",
      });
    } catch (e) {
      const message = errorMessage(e);
      setSnackbar({
        open: true,
        message: message || "Failed to save settings",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600 }}>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        Message settings
      </Typography>
      <FormGroup>
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.isTypingIndicatorEnabled}
                onChange={handleToggleChange("isTypingIndicatorEnabled")}
                disabled={loading || saving}
              />
            }
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <ChatBubbleOutlineIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    Show typing indicator
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Let customers see when you are typing a reply in conversations.
                  </Typography>
                </Box>
              </Stack>
            }
          />
        </Box>

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={settings.isChatSimulationEnabled}
                onChange={handleToggleChange("isChatSimulationEnabled")}
                disabled={loading || saving}
              />
            }
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <ChatBubbleOutlineIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    Enable chat simulation
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Allow team inbox simulation mode without calling WhatsApp API.
                  </Typography>
                </Box>
              </Stack>
            }
          />
        </Box>

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={settings.isBlueTickEnabled}
                onChange={handleToggleChange("isBlueTickEnabled")}
                disabled={loading || saving}
              />
            }
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <DoneAllIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    Auto blue tick / read receipts
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Automatically mark messages as read and show blue ticks to customers.
                  </Typography>
                </Box>
              </Stack>
            }
          />
        </Box>
      </FormGroup>
      <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || saving}
        >
          {settings.id ? "Save changes" : "Save"}
        </Button>
      </Stack>
      <Snackbar
              open={snackbar.open}
              autoHideDuration={3000}
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
              <Alert
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                severity={snackbar.severity}
                sx={{ borderRadius: 2 }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
    </Box>
  );
}

function WhatsappSyncSettings() {
  const { whatsapp, handleSync } = useWhatsappEmbeddedSyncState();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const onSync = async (type) => {
    try {
      await handleSync(type);
    } catch (e) {
      const message = errorMessage(e);
      setSnackbar({
        open: true,
        message: message || "Something went wrong",
        severity: "error",
      });
    }
  };

  if (whatsapp.isEmbeddedSignup !== 1) {
    return (
      <Box sx={{ maxWidth: 700 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          WhatsApp data sync
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Contact and chat history sync is available after you connect with Facebook using embedded signup (WhatsApp Business app onboarding).
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 700 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        WhatsApp data sync
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Sync your WhatsApp Business contacts and chat history. Progress updates while history sync is running.
      </Typography>
      <WhatsappEmbeddedSyncContent
        isContact={whatsapp.isContact}
        isHistory={whatsapp.isHistory}
        chunkData={whatsapp.chunkData}
        onSync={onSync}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function SettingsView() {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname.replace("/settings", "").replace(/^\//, "") || SETTINGS_TABS[0].path;
  const activeTab = SETTINGS_TABS.find((t) => t.path === path)?.path || SETTINGS_TABS[0].path;

  const handleTabChange = (_, value) => {
    navigate(value ? `/settings/${value}` : "/settings");
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
          Settings
        </Typography>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 40,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 500 },
          }}
        >
          {SETTINGS_TABS.map((tab) => (
            <Tab key={tab.id} label={tab.label} value={tab.path} />
          ))}
        </Tabs>
      </Box>
      <Divider />
      <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
        <Routes>
          <Route path="payment-configuration" element={<PaymentConfiguration />} />
          <Route path="message-settings" element={<MessageSettings />} />
          <Route path="catalog" element={<CatalogSettings />} />
          <Route path="whatsapp-sync" element={<WhatsappSyncSettings />} />
          <Route path="call-settings" element={<CallSettings />} />
          <Route path="api-keys" element={<ApiKeysSettings />} />
          <Route path="inbound-email" element={<Navigate to="/inbound-email" replace />} />
          <Route path="" element={<Navigate to="payment-configuration" replace />} />
          <Route path="*" element={<Navigate to="payment-configuration" replace />} />
        </Routes>
      </Box>
    </Box>
  );
}

export default SettingsView;
